'use client'

import {
  ArrowUpRight,
  ChevronDown,
  ExternalLink,
  Heart,
  MessageCircle,
  Repeat2,
} from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import type { RecentTweetsResult } from 'sprites-types/workflow/tools/recentTweets'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'

type Tweet = RecentTweetsResult[number]

interface TweetItemProps {
  tweet: Tweet
  className?: string
}

export function TweetItem({ tweet, className }: TweetItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const createdAt = new Date(tweet.created_at)
  const dateLabel = isNaN(createdAt.getTime()) ? tweet.created_at : createdAt.toLocaleString()

  const media = tweet.media?.filter((m) => Boolean(m.url)) || []

  const toggleExpanded = () => setIsExpanded((prev) => !prev)

  const textPreview = tweet.text.length > 160 ? tweet.text.slice(0, 160) + '…' : tweet.text

  const getHostname = (raw?: string) => {
    if (!raw) return undefined
    try {
      const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`)
      return u.hostname.replace(/^www\./, '')
    } catch {
      return undefined
    }
  }

  const tweetUrl = `https://x.com/${tweet.author_username}/status/${tweet.id}`

  // Context actions for individual tweets
  const contextActions: ContextAction[] = [
    {
      id: 'open-tweet',
      label: 'Open Tweet',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: () => window.open(tweetUrl, '_blank'),
    },
  ]

  const MetricPill = ({
    icon,
    count,
    color,
    title,
    size = 'sm',
  }: {
    icon: React.ReactNode
    count: number
    color: 'blue' | 'green' | 'rose'
    title: string
    size?: 'sm' | 'md'
  }) => {
    const sizeClasses = size === 'sm' ? 'text-[11px] px-2 py-0.5' : 'text-xs px-2.5 py-1'
    const colorClasses =
      color === 'blue'
        ? 'hover:bg-blue-50 text-gray-700 hover:text-blue-600 border-gray-200/70'
        : color === 'green'
          ? 'hover:bg-green-50 text-gray-700 hover:text-green-600 border-gray-200/70'
          : 'hover:bg-rose-50 text-gray-700 hover:text-rose-600 border-gray-200/70'
    return (
      <span
        title={title}
        aria-label={title}
        className={`inline-flex items-center gap-1 rounded-full border ${sizeClasses} ${colorClasses}`}
      >
        <span className="inline-flex">{icon}</span>
        <span className="tabular-nums">{count}</span>
      </span>
    )
  }

  return (
    <ContextMenuWrapper actions={contextActions}>
      <article
        className={`rounded-3xl border border-gray-200/50 bg-white p-4 text-black shadow-sm backdrop-blur-[8px] ${className}`}
      >
        <button
          type="button"
          onClick={toggleExpanded}
          className="w-full text-left"
          aria-expanded={isExpanded}
          aria-controls={`tweet-${tweet.id}-content`}
        >
          <header className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-semibold">{tweet.author_name}</span>
                <span className="truncate text-gray-600">@{tweet.author_username}</span>
                {tweet.author_verified && (
                  <span title="Verified" aria-label="Verified" className="text-blue-500">
                    ✔︎
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500">{dateLabel}</div>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <MetricPill
                icon={<Heart className="h-3.5 w-3.5" />}
                count={tweet.public_metrics.like_count}
                color="rose"
                title="Likes"
                size="sm"
              />
              <MetricPill
                icon={<Repeat2 className="h-3.5 w-3.5" />}
                count={tweet.public_metrics.retweet_count}
                color="green"
                title="Retweets"
                size="sm"
              />
              <MetricPill
                icon={<MessageCircle className="h-3.5 w-3.5" />}
                count={tweet.public_metrics.reply_count}
                color="blue"
                title="Replies"
                size="sm"
              />
              <ChevronDown
                className={`ml-1 h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </header>

          {!isExpanded && (
            <div className="mt-2 text-sm text-black">
              <div className="truncate">{textPreview}</div>
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                {media.length > 0 && <span>{media.length} media</span>}
                {tweet.entities?.urls?.length ? (
                  <span>{tweet.entities.urls.length} links</span>
                ) : null}
              </div>
            </div>
          )}
        </button>

        {isExpanded && (
          <div id={`tweet-${tweet.id}-content`} className="mt-3">
            <p className="whitespace-pre-wrap break-words text-black">{tweet.text}</p>

            {media.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {media.map((item) => (
                  <a
                    key={item.media_key}
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={item.url} alt={item.type} className="h-auto w-full rounded-2xl" />
                  </a>
                ))}
              </div>
            )}

            {tweet.entities?.urls?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {tweet.entities.urls.map((u, i) => {
                  const raw = u.expanded_url || u.url
                  const host = getHostname(raw)
                  return (
                    <li key={`${tweet.id}-url-${i}`} className="text-sm">
                      <a
                        href={raw}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 break-all rounded-2xl border border-gray-200/70 px-3 py-1.5 text-blue-700 hover:bg-gray-50"
                      >
                        <span className="font-medium">{host || u.display_url}</span>
                        <ExternalLink className="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
                      </a>
                    </li>
                  )
                })}
              </ul>
            )}

            <div className="mt-3">
              <a
                href={tweetUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl border border-gray-200/70 px-3 py-1.5 text-blue-700 hover:bg-gray-50"
              >
                <span className="font-medium">Open in X</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
              </a>
            </div>
          </div>
        )}
      </article>
    </ContextMenuWrapper>
  )
}
